import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authHelper } from '../utils/authHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check if we have a token
    if (!authHelper.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: authHelper.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        // Token invalid or expired
        authHelper.removeToken();
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      authHelper.removeToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData, token) => {
    if (token) {
      authHelper.saveToken(token);
    }
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    authHelper.removeToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, checkAuth, setUser }),
    [user, loading, login, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
